import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Sparkles, Globe, Users, TrendingUp, Zap, Crown, CheckCircle, Shield, Clock, Award, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import Footer from '@/components/Footer';
import Testimonials from '@/components/Testimonials';
import FAQ from '@/components/FAQ';

const Index = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = (file: File | null) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    console.log('Uploading file:', file.name);
    
    // Store image for analysis
    const reader = new FileReader();
    reader.onload = (e) => {
      localStorage.setItem('uploadedImage', e.target?.result as string);
      navigate('/analysis');
    };
    reader.readAsDataURL(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-500/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [0, -90, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 15, repeat: Infinity }}
          className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-500/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            y: [0, -50, 0],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/20 rounded-full blur-3xl"
        />
      </div>

      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="relative">
              <Sparkles className="h-9 w-9 text-purple-400" />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 bg-purple-400 rounded-full blur-xl"
              />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              AttractivenessAI
            </h1>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')} 
              className="border-white/20 bg-white/5 hover:bg-white/10 text-white backdrop-blur-xl"
            >
              Dashboard
            </Button>
          </motion.div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-6 py-20 relative z-10">
        {/* Announcement Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              boxShadow: [
                '0 0 20px rgba(168, 85, 247, 0.4)',
                '0 0 40px rgba(168, 85, 247, 0.6)',
                '0 0 20px rgba(168, 85, 247, 0.4)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block"
          >
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-3 rounded-full border-2 border-purple-400/50 shadow-2xl">
              <p className="text-white font-bold flex items-center gap-3 text-lg">
                <Zap className="h-6 w-6 animate-pulse" />
                🔥 Limited Time: Complete Analysis for Only $9.99!
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-12 max-w-5xl mx-auto"
        >
          <h2 className="text-6xl md:text-8xl font-black mb-8 leading-tight">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Discover Your
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Global Appeal
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-gray-300 mb-6 font-medium leading-relaxed">
            AI-powered analysis reveals your attractiveness across
            <br />
            <span className="text-purple-400 font-bold">50+ cities</span> and <span className="text-pink-400 font-bold">multiple demographics</span> worldwide
          </p>
          <div className="flex items-center justify-center gap-3 text-lg text-gray-400">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-400" />
              <span>60 seconds</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-400" />
              <span>100% Private</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-400" />
              <span>AI-Powered</span>
            </div>
          </div>
        </motion.div>

        {/* Upload Section - Enhanced */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-3xl mx-auto mb-20"
        >
          <div
            className={`relative rounded-3xl p-16 transition-all duration-500 ${
              dragActive 
                ? 'bg-gradient-to-br from-purple-600/40 to-pink-600/40 scale-105 shadow-2xl shadow-purple-500/50' 
                : 'bg-gradient-to-br from-purple-600/20 to-pink-600/20 hover:from-purple-600/30 hover:to-pink-600/30'
            } backdrop-blur-xl border-2 ${
              dragActive ? 'border-purple-400' : 'border-white/20'
            } shadow-2xl`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {/* Animated border glow */}
            <motion.div
              animate={{ 
                opacity: [0.5, 1, 0.5],
                scale: [1, 1.02, 1]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 opacity-20 blur-xl"
            />

            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="image/*"
              onChange={handleChange}
            />
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center cursor-pointer relative z-10"
            >
              <motion.div
                animate={{ 
                  y: [0, -15, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="relative mb-8"
              >
                <div className="relative">
                  <Upload className="h-28 w-28 text-purple-300" />
                  <motion.div
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-purple-400 rounded-full blur-2xl"
                  />
                </div>
              </motion.div>
              
              <h3 className="text-4xl font-bold mb-4 text-white">
                Upload Your Photo
              </h3>
              <p className="text-xl text-gray-300 mb-8">
                Drag & drop or click to select
              </p>
              
              <Button 
                className="h-16 px-12 text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-2xl shadow-purple-500/50 hover:shadow-purple-500/70 transition-all hover:scale-105"
              >
                <Upload className="h-6 w-6 mr-3" />
                Choose Photo
              </Button>
            </label>
          </div>
          <p className="text-center text-sm text-gray-400 mt-6 flex items-center justify-center gap-2">
            <Shield className="h-4 w-4 text-green-400" />
            Your privacy is protected. Photos are analyzed securely and never shared.
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mb-20"
        >
          {[
            { number: '50+', label: 'Global Cities', icon: Globe },
            { number: '16', label: 'Venue Types', icon: MapPin },
            { number: '4.8/5', label: 'User Rating', icon: Award },
            { number: '60s', label: 'Analysis Time', icon: Clock }
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all hover:scale-105"
            >
              <stat.icon className="h-8 w-8 text-purple-400 mb-3 mx-auto" />
              <p className="text-3xl font-bold text-white mb-1">{stat.number}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Features Grid - Enhanced */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          {[
            {
              icon: Globe,
              title: 'Global Ratings',
              description: 'See how you rank in 50+ cities from Tokyo to Miami',
              gradient: 'from-purple-600 to-blue-600'
            },
            {
              icon: Users,
              title: 'Demographic Insights',
              description: 'Discover which demographics find you most attractive',
              gradient: 'from-pink-600 to-purple-600'
            },
            {
              icon: TrendingUp,
              title: 'Improvement Tips',
              description: 'Get personalized recommendations to boost your appeal',
              gradient: 'from-blue-600 to-cyan-600'
            }
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              className="group relative"
            >
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all hover:scale-105 h-full">
                <div className={`bg-gradient-to-br ${feature.gradient} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">{feature.title}</h3>
                <p className="text-gray-300 text-lg leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pricing Section - Enhanced */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="max-w-5xl mx-auto mb-20"
        >
          <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 backdrop-blur-xl rounded-3xl p-12 border-2 border-purple-400/50 shadow-2xl">
            <div className="text-center mb-10">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Crown className="h-20 w-20 text-yellow-400 mx-auto mb-6" />
              </motion.div>
              <h3 className="text-5xl font-bold mb-4 text-white">Choose Your Plan</h3>
              <p className="text-2xl text-gray-300">Get your complete analysis today</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* One-Time Plan */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-purple-600/40 to-pink-600/40 backdrop-blur-xl rounded-2xl p-8 border-2 border-purple-400/50 relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-4 py-2 rounded-full animate-pulse">
                  🔥 BEST DEAL
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="h-8 w-8 text-yellow-400" />
                  <h4 className="text-3xl font-bold text-white">One-Time</h4>
                </div>
                <div className="mb-6">
                  <p className="text-6xl font-black text-white mb-2">$9.99</p>
                  <p className="text-lg text-gray-300">Pay once, keep forever</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {['Complete analysis', '50+ cities worldwide', 'All demographics', 'Lifetime access'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-white">
                      <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full h-14 text-lg font-bold bg-white text-purple-600 hover:bg-gray-100">
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
              
              {/* Monthly Plan */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-600/40 to-purple-600/40 backdrop-blur-xl rounded-2xl p-8 border-2 border-blue-400/50"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Crown className="h-8 w-8 text-yellow-400" />
                  <h4 className="text-3xl font-bold text-white">Monthly Pro</h4>
                </div>
                <div className="mb-6">
                  <p className="text-6xl font-black text-white mb-2">
                    $15<span className="text-3xl">/mo</span>
                  </p>
                  <p className="text-lg text-gray-300">Cancel anytime</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {['Everything in One-Time', 'NEW advice every month', 'Target demographics', 'Priority support'].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-white">
                      <CheckCircle className="h-5 w-5 text-blue-400 flex-shrink-0" />
                      <span className="text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full h-14 text-lg font-bold bg-white text-blue-600 hover:bg-gray-100">
                  Start Monthly <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Testimonials */}
        <div className="mb-20">
          <Testimonials />
        </div>

        {/* FAQ */}
        <FAQ />

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="text-center mt-16"
        >
          <p className="text-gray-400 mb-4 text-lg font-semibold">Trusted by users worldwide</p>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-500">
            <div>⭐⭐⭐⭐⭐ 4.8/5.0</div>
            <div>•</div>
            <div>AI-Powered Analysis</div>
            <div>•</div>
            <div>30-Day Guarantee</div>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Index;